import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useNavigate } from 'react-router-dom';
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import AnimatedBackground from '../../utils/AnimatedBackground';
import Footer from '../../utils/Footer';
import TopBar from '../../utils/TopBar';
import { formatNumber, useIncomeEdit } from '../hooks/EditIncome';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const EditIncome = () => {
  const navigate = useNavigate();
  const {
    incomeList,
    selectedIncome,
    handleInputChange,
    handleInputBlur,
    handleSubmit,
    handleIncomeSelect,
    allIncomeData,
    handleReset
  } = useIncomeEdit();

  const getInputValue = (field: string, value: string | number | boolean): string => {
    if (field === 'document_type') return value as string;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return formatNumber(value as number);
  };

  const onSubmit = (e: React.FormEvent) => {
    handleSubmit(e);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
      <AnimatedBackground />
      <div className="relative z-10 flex flex-col min-h-screen">
        <TopBar 
          title="Income Management"
          subtitle="Manage and analyze your income with ease"
        />
        <main className="relative z-10 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Card className="bg-[#0f1115] border-gray-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Income Details</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleReset} 
                    className="bg-blue-600 hover:bg-blue-700"
                    type="button"
                  >
                    Show All
                  </Button>
                  <Select onValueChange={handleIncomeSelect} value={selectedIncome?.date || ""}>
                    <SelectTrigger className="w-[180px] bg-[#0f1115] text-white border-gray-700">
                      <SelectValue placeholder="Select Income Month" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1115] text-white border border-gray-700">
                      {allIncomeData.map((income) => (
                        <SelectItem 
                          key={income.date} 
                          value={income.date}
                          className="text-gray-400 transition-colors hover:text-white hover:bg-[#1f2937] data-[highlighted]:text-white data-[highlighted]:bg-[#1f2937] data-[state=checked]:text-white data-[state=checked]:bg-[#1f2937] cursor-pointer"
                        >
                          {new Date(income.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={onSubmit}>
                  <div className="overflow-x-auto">
                    <Table className="text-white">
                      <TableHeader>
                        <TableRow className="border-gray-800">
                          {[
                            'Date',
                            'Document Type',
                            'Gross Income',
                            'Net Income',
                            'VR',
                            'Other Gross Income Value',
                            'Other Income Taxes',
                            'Other Net Income',
                            'Monthly Income'
                          ].map((header) => (
                            <TableHead key={header} className="text-gray-400">{header}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {incomeList
                          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                          .map((income) => (
                            <TableRow 
                              key={income.date} 
                              className={`cursor-pointer border-gray-800 hover:bg-gray-900 ${
                                income.date === selectedIncome?.date ? 'bg-gray-800' : ''
                              }`}
                              onClick={() => handleIncomeSelect(income.date)}
                            >
                              {[
                                'date',
                                'document_type',
                                'gross_income',
                                'net_income',
                                'vr',
                                'other_gross_income_value',
                                'other_income_taxes',
                                'other_net_income',
                                'monthly_income'
                              ].map((field) => (
                                <TableCell key={field} className="text-gray-300">
                                  {field === 'date' ? (
                                    <span>
                                      {new Date(income[field]).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                    </span>
                                  ) : field === 'other_gross_income' || field === 'other_income_taxed' ? (
                                    <span>{income[field] ? 'Yes' : 'No'}</span>
                                  ) : (
                                    income.date === selectedIncome?.date ? (
                                      <Input
                                        type="text"
                                        name={field}
                                        value={getInputValue(field, selectedIncome[field])}
                                        onChange={handleInputChange}
                                        onBlur={handleInputBlur}
                                        className="w-full bg-gray-900 text-white border-gray-700"
                                        placeholder={field === 'document_type' ? 'Enter document type' : '0.00'}
                                        disabled={[
                                          'date',  // Only disable these fields
                                          'net_income',
                                          'other_income_taxes',
                                          'other_net_income',
                                          'monthly_income'
                                        ].includes(field)}
                                      />
                                    ) : (
                                      <span>
                                        {field === 'document_type' ? income[field] : formatNumber(income[field] as number)}
                                      </span>
                                    )
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-5 flex justify-between">
                    <Button 
                      type="button" 
                      onClick={() => navigate('/income/dashboard')}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Back
                    </Button>
                    <Button 
                      type="submit"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Save Changes
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default EditIncome;

